import { useState } from 'react'
import { Outlet, useParams } from 'react-router-dom'

import { useLiveUpdates } from '../../live/events'
import { t } from '../../i18n'
import { AddChat } from './AddChat'
import { SendingOff } from './SendingOff'
import { ThreadList } from './ThreadList'
import { LiveIndicator } from './LiveIndicator'

/**
 * The screen a manager leaves open all day: the list on the left, whatever they
 * are working on beside it.
 *
 * The list stays mounted while conversations are opened and closed. That is the
 * point of the nested route: the scroll position, the filter and the live stream
 * all survive, so going back to the inbox is instant rather than a reload.
 */
export function InboxLayout() {
  const { threadId } = useParams<{ threadId: string }>()
  const [filter, setFilter] = useState<InboxFilter>('all')

  // One stream for the whole screen. It follows the open conversation, because
  // draft events are only sent for a named thread.
  const live = useLiveUpdates(threadId ?? null)

  return (
    <div className="inbox">
      <section className="inbox__list" aria-label={t('inbox.title')}>
        <header className="inbox__header">
          <h1 className="inbox__title">{t('inbox.title')}</h1>
          <LiveIndicator status={live} />
          {/*
            The one way a conversation enters this list other than somebody
            writing to us (docs/adr/0012). It is here rather than in the settings
            because this is the screen a manager is on when they notice a client
            is missing.
          */}
          <AddChat />
        </header>
        {/*
          Above the list, because it changes what every row on it means: while
          sending is off, approving a draft queues something that will not go.
        */}
        <SendingOff />
        <ThreadList filter={filter} onFilterChange={setFilter} selected={threadId ?? null} />
      </section>

      <section className="inbox__detail">
        <Outlet />
      </section>
    </div>
  )
}

export type InboxFilter = 'all' | 'unread' | 'handoff' | 'paused'

/** What the chosen tab asks the server for. */
export function filterQuery(filter: InboxFilter): {
  mode?: string
  unreadOnly?: boolean
  waitingOnly?: boolean
} {
  switch (filter) {
    case 'unread':
      return { unreadOnly: true }
    case 'handoff':
      // Conversations with work open against them, not a mode. Nothing writes
      // handoff_pending — the pause control is the column's only writer and it
      // may set two modes, neither of them that one — so this tab was empty
      // whatever was happening, which is the one thing it must never be.
      return { waitingOnly: true }
    case 'paused':
      return { mode: 'human_paused' }
    case 'all':
      return {}
  }
}

/** Shown in the right-hand pane before a conversation is chosen. */
export function NoThreadChosen() {
  return (
    <div className="empty-pane">
      <p className="empty-pane__title">{t('inbox.pickThread')}</p>
      <p className="empty-pane__hint">{t('inbox.pickThreadHint')}</p>
    </div>
  )
}
